import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { deepEqual } from 'fast-equals';
import type { Subscription } from 'rxjs';
import { merge, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';

import {
  DividerComponent,
  HeadingComponent,
  IconComponent,
  MessageComponent,
  RtStatefulComponent,
} from '@recruitee/user-interface';

import type { DynamicForm } from './dynamic-form';
import { Destroyable, takeUntilDestroyed } from '../../../common';
import { DynamicHelpers } from '../../helpers';
import type { AnyDynamicInput, DynamicGroupConfig, DynamicText } from '../../types';
import { DynamicInputConfigType } from '../../types';
import type { CountrySelectInput } from '../country-select-input/country-select-input.component';
import { DynamicListInfoComponent } from '../dynamic-list-info/dynamic-list-info.component';
import type { RegionSelectInput } from '../region-select-input/region-select-input.component';
import type { DynamicFormAdditionalGroups } from './dynamic-form-additional-groups/dynamic-form-additional-groups';
import { DynamicFormAdditionalGroupsComponent } from './dynamic-form-additional-groups/dynamic-form-additional-groups-dropdown.component';
import type { DynamicFormGroup } from './dynamic-form-group/dynamic-form-group';
import { DynamicFormGroupComponent } from './dynamic-form-group/dynamic-form-group.component';
import type { DynamicFormInput } from './dynamic-form-input/dynamic-form-input';
import { DynamicFormInputComponent } from './dynamic-form-input/dynamic-form-input.component';
import { DynamicFormLocationsService } from './dynamic-form-locations-service';
import { DynamicFormResourcesService } from './dynamic-form-resources.service';

type State = {
  selectedAdditionalGroupsNames: string[];
};

@Component({
  selector: 'rti-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: DynamicFormResourcesService, useClass: DynamicFormResourcesService },
    { provide: DynamicFormLocationsService, useClass: DynamicFormLocationsService },
  ],
  imports: [
    DividerComponent,
    DynamicFormAdditionalGroupsComponent,
    DynamicFormGroupComponent,
    DynamicFormInputComponent,
    DynamicListInfoComponent,
    HeadingComponent,
    IconComponent,
    MessageComponent,
    ReactiveFormsModule,
  ],
})
@Destroyable()
export class DynamicFormComponent extends RtStatefulComponent<State> {
  @Input() public form: DynamicForm;

  public additionalGroups: DynamicFormAdditionalGroups;

  private locationSelectsSubscription: Subscription | undefined;
  private resourceSelectsSubscription: Subscription | undefined;
  private inputsSubscription: Subscription | undefined;

  constructor(
    public cdRef: ChangeDetectorRef,
    private helpers: DynamicHelpers,
    private locationSvc: DynamicFormLocationsService,
    private resourcesSvc: DynamicFormResourcesService,
  ) {
    super(cdRef);
    this.createState({ selectedAdditionalGroupsNames: [] });
  }

  public ngOnInit(): void {
    this.initForm();
    this.initFormLoader();
    this.initResourceFormConfigChanges();
    this.initResourceFormLoading();
  }

  public buildText(text: DynamicText): string {
    return this.helpers.buildText(text);
  }

  public trackInput(index: number, _input: DynamicForm['inputs'][0]): string | number {
    return (<DynamicGroupConfig>_input.config)?.checksum || index;
  }

  public isHidden(input: AnyDynamicInput): boolean {
    return !!('hidden' in input.config && input.config.hidden);
  }

  public additionalGroupSelected(name: string): void {
    const groupConfig = this.additionalGroups.config.groups.find(g => g.name === name)!;
    this.setState({
      ...this.state,
      selectedAdditionalGroupsNames: [...this.state.selectedAdditionalGroupsNames, name],
    });
    this.form.addAdditionalGroup(groupConfig);
  }

  public removeInput(input: DynamicFormInput): void {
    const name = input.config.name;

    this.setState({
      ...this.state,
      selectedAdditionalGroupsNames: this.state.selectedAdditionalGroupsNames.filter(
        n => n !== name,
      ),
    });
    this.form.removeGroup(name);
  }

  public removeGroup(group: DynamicFormGroup): void {
    const name = group.config.name;

    this.setState({
      ...this.state,
      selectedAdditionalGroupsNames: this.state.selectedAdditionalGroupsNames.filter(
        n => n !== name,
      ),
    });
    this.form.removeGroup(name);
  }

  private initForm(): void {
    if (this.resourceSelectsSubscription) this.resourceSelectsSubscription.unsubscribe();
    this.resourceSelectsSubscription = this.initResourceSelects();

    this.initLocationSelectsSubscription();

    this.initAdditionalGroup();

    if (this.inputsSubscription) this.inputsSubscription.unsubscribe();
    this.inputsSubscription = this.form.inputs$
      .pipe(takeUntilDestroyed(this))
      .subscribe(_inputs => {
        this.cdRef.detectChanges();

        this.initLocationSelectsSubscription();
      });
  }

  private initLocationSelectsSubscription(): void {
    if (this.locationSelectsSubscription) this.locationSelectsSubscription.unsubscribe();
    this.locationSelectsSubscription = this.initLocationSelects();
  }

  private initFormLoader(): Subscription {
    return merge(of(this.form.value), this.form.valueChanges)
      .pipe(takeUntilDestroyed(this), distinctUntilChanged(deepEqual))
      .subscribe(formValue => {
        const formLoaderInputs = <DynamicFormInput[]>(
          this.form.inputs.filter(input => input.type === DynamicInputConfigType.FORM_LOADER)
        );

        if (formLoaderInputs.length > 0) {
          this.resourcesSvc.fetchForm(formValue);
        }
      });
  }

  private deepFindInput(inputs: AnyDynamicInput[], type: DynamicInputConfigType): any {
    return (
      inputs.find(input => input.type === type) ||
      inputs
        .map(input => {
          if (`inputs` in input) {
            return this.deepFindInput(input.inputs, type);
          } else {
            return false;
          }
        })
        .find(input => input && 'type' in input && input.type === type)
    );
  }

  private initLocationSelects(): Subscription | undefined {
    const countrySelect = <CountrySelectInput>(
      this.deepFindInput(this.form.inputs, DynamicInputConfigType.COUNTRY_SELECT)
    );

    if (!countrySelect) return;

    const regionSelectInput = <RegionSelectInput>(
      this.deepFindInput(this.form.inputs, DynamicInputConfigType.REGION_SELECT)
    );

    if (!regionSelectInput) return;

    return merge(of(this.form.value), this.form.valueChanges)
      .pipe(
        takeUntilDestroyed(this),
        map(_formValue => countrySelect.control.value),
        distinctUntilChanged(deepEqual),
      )
      .subscribe(countryCode => {
        if (!!countryCode) this.locationSvc.getRegionsCollection(countryCode);
      });
  }

  private initResourceSelects(): Subscription | undefined {
    const resourceSelectInputs = <DynamicFormInput[]>this.findResourceSelects(this.form);

    if (resourceSelectInputs.length === 0) return;

    return merge(of(this.form.value), this.form.valueChanges)
      .pipe(
        takeUntilDestroyed(this),
        debounceTime(300),
        map(_formValue => this.resourceSelectValues(resourceSelectInputs)),
        distinctUntilChanged(deepEqual),
      )
      .subscribe(_resourceSelectValues => {
        this.resourcesSvc.fetchResources(this.form.value);
      });
  }

  private findResourceSelects(groupOrInput: DynamicForm | AnyDynamicInput): AnyDynamicInput[] {
    if ('inputs' in groupOrInput) {
      return groupOrInput.inputs.flatMap(input => this.findResourceSelects(input));
    } else if (groupOrInput.type === DynamicInputConfigType.RESOURCE_SELECT) {
      return [groupOrInput];
    } else {
      return [];
    }
  }

  private initResourceFormConfigChanges(): Subscription {
    return this.resourcesSvc.formConfig$
      .pipe(
        filter(config => !!config),
        takeUntilDestroyed(this),
      )
      .subscribe(config => {
        this.form.init(config);
        this.initForm();
        this.form.triggerValidation();
      });
  }

  private initResourceFormLoading(): Subscription {
    return this.resourcesSvc.loading$.pipe(takeUntilDestroyed(this)).subscribe(this.form.loading$);
  }

  private resourceSelectValues(resourceSelectInputs: DynamicFormInput[]): {} {
    return resourceSelectInputs.reduce(
      (acc, input) => {
        acc[input.config.name] = input.control.value;
        return acc;
      },
      <any>{},
    );
  }

  private initAdditionalGroup(): void {
    const allAdditionalGroups = <DynamicFormAdditionalGroups[]>(
      this.form.inputs.filter(input => input.type === DynamicInputConfigType.ADDITIONAL_GROUPS)
    );

    if (allAdditionalGroups.length === 0) {
      this.additionalGroups = null!;
      return;
    }
    if (allAdditionalGroups.length > 1) throw new Error('Form has more than 1 additional group');

    this.additionalGroups = allAdditionalGroups[0];
  }
}
